import { useEffect, useRef, type RefObject } from 'react';
import { useKeyDown } from './use-key-down';

interface UseFocusTrapOptions {
   enabled?: boolean;
   initialFocusRef?: RefObject<HTMLElement | null>;
}

/**
 * Hook to trap focus within a container element.
 * Handles Tab and Shift+Tab navigation to keep focus cycling within the container.
 *
 * @param options Configuration for focus trap behavior
 * @returns Ref to attach to the container element
 *
 * @example
 * ```tsx
 * function Dialog({ isOpen }) {
 *   const closeButtonRef = useRef<HTMLButtonElement>(null);
 *   const containerRef = useFocusTrap({
 *     enabled: isOpen,
 *     initialFocusRef: closeButtonRef
 *   });
 *
 *   return (
 *     <div ref={containerRef} role="dialog">
 *       <button ref={closeButtonRef}>Close</button>
 *       <input type="text" />
 *       <button>Submit</button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useFocusTrap<T extends HTMLElement>(options: UseFocusTrapOptions = {}) {
   const { enabled = true, initialFocusRef } = options;
   const containerRef = useRef<T>(null);
   const previousActiveElement = useRef<HTMLElement | null>(null);

   useKeyDown(
      event => {
         if (event.key !== 'Tab') return;

         const container = containerRef.current;
         if (!container) return;

         const focusableElements = getFocusableElements(container);
         if (focusableElements.length === 0) {
            console.warn(
               'Focus trap contains no focusable elements. This is likely a bug - containers should have at least a close button.'
            );
            event.preventDefault(); // Still trap focus to maintain consistent behavior
            return;
         }

         const firstElement = focusableElements[0];
         const lastElement = focusableElements[focusableElements.length - 1];
         const activeElement = document.activeElement;

         if (event.shiftKey && activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
         } else if (!event.shiftKey && activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
         }
      },
      { enabled }
   );

   useEffect(() => {
      if (!enabled) return;

      previousActiveElement.current = document.activeElement as HTMLElement;

      const container = containerRef.current;
      if (!container) return;

      if (initialFocusRef?.current) {
         initialFocusRef.current.focus();
      } else {
         const firstFocusable = getFocusableElements(container)[0];
         if (firstFocusable) {
            firstFocusable.focus();
         } else {
            container.focus();
         }
      }

      return () => {
         previousActiveElement.current?.focus();
      };
   }, [enabled, initialFocusRef]);

   return containerRef;
}

function getFocusableElements(container: HTMLElement): HTMLElement[] {
   const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
   ].join(',');

   return Array.from(container.querySelectorAll(focusableSelectors));
}
